import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Tabs,
  Tab,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getStripe, isStripeConfigured } from '../../lib/stripe';
import { createPosPaymentIntent, recordCashPayment, getPosPayment } from '../../services/api';
import { toast } from '../../lib/toast';
import { formatPrice } from '../../utils/formatPrice';
import { newClientRequestId } from '../../utils/clientRequestId';

const delay = (ms) => new Promise((r) => setTimeout(r, ms));
const TERMINAL = new Set(['succeeded', 'failed', 'refunded']);

// Resolve a card payment's final state. The webhook — not the browser — is
// authoritative, so we wait for the server's confirmation: prefer the realtime
// `pos_payment_updated` event (delivered via subscribe), and fall back to a short
// poll only if the socket is down. Each poll iteration bails the instant the
// socket resolves, so the happy path makes ~0–1 extra requests instead of twelve.
function awaitPaymentResult({ restaurantId, paymentId, subscribe }) {
  return new Promise((resolve) => {
    let settled = false;
    let unsubscribe = () => {};
    const finish = (val) => {
      if (settled) return;
      settled = true;
      try { unsubscribe(); } catch (_) {}
      resolve(val);
    };

    if (subscribe) {
      unsubscribe = subscribe((d) => {
        if (Number(d?.paymentId) === Number(paymentId) && TERMINAL.has(d?.status)) finish(d);
      });
    }

    (async () => {
      for (let i = 0; i < 12 && !settled; i++) {
        await delay(1000);
        if (settled) return;
        try {
          const { data } = await getPosPayment(restaurantId, paymentId);
          if (TERMINAL.has(data.status)) return finish(data);
        } catch (_) { /* keep trying */ }
      }
      finish(null);
    })();
  });
}

const CardForm = ({ restaurantId, sessionId, owed, currency, onPaid, onSubscribePayments }) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const pay = async () => {
    if (!stripe || !elements) return;
    setError('');
    setProcessing(true);
    try {
      const { data } = await createPosPaymentIntent(restaurantId, sessionId);
      const { clientSecret, paymentId } = data;
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });
      if (result.error) {
        setError(result.error.message || t('pos.payment.cardFailed', 'Card payment failed.'));
        setProcessing(false);
        return;
      }
      // Confirm via the server (webhook-driven) rather than trusting the client.
      const finalState = await awaitPaymentResult({ restaurantId, paymentId, subscribe: onSubscribePayments });
      if (finalState && finalState.status === 'succeeded') {
        toast.success(t('pos.payment.cardSuccess', 'Card payment received'));
        onPaid();
      } else if (finalState && finalState.status === 'failed') {
        setError(t('pos.payment.cardFailed', 'Card payment failed.'));
      } else {
        // Optimistic: Stripe confirmed but our webhook hasn't landed yet.
        toast(t('pos.payment.cardPending', 'Payment is processing — it will update shortly.'));
        onPaid();
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 503) setError(t('pos.payment.cardUnavailable', 'Card payments are not configured on this server.'));
      else setError(err.response?.data?.error || t('pos.payment.cardFailed', 'Card payment failed.'));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          p: 1.75,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          mb: 2,
          backgroundColor: '#fff',
        }}
      >
        <CardElement options={{ hidePostalCode: true, style: { base: { fontSize: '16px', color: '#0F172A' } } }} />
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Button fullWidth variant="contained" onClick={pay} disabled={!stripe || processing}>
        {processing ? <CircularProgress size={22} color="inherit" /> : `${t('pos.payment.payCard', 'Pay')} ${formatPrice(owed, currency)}`}
      </Button>
    </Box>
  );
};

/**
 * Settle a tab. Cash always available; the Card tab appears only when a Stripe
 * publishable key is configured. Amounts are recomputed server-side — the owed
 * figure shown here is only a display value.
 */
const PaymentDialog = ({ open, restaurantId, sessionId, owed = 0, currency, onClose, onPaid, onSubscribePayments }) => {
  const { t, i18n } = useTranslation();
  const cardEnabled = isStripeConfigured();
  const [method, setMethod] = useState('cash');
  const [tendered, setTendered] = useState('');
  const [busy, setBusy] = useState(false);

  // Reset transient state whenever the dialog opens.
  React.useEffect(() => {
    if (open) {
      setMethod('cash');
      setTendered(String(owed.toFixed ? owed.toFixed(2) : owed));
    }
  }, [open, owed]);

  const tenderedNum = parseFloat(tendered);
  const change = !Number.isNaN(tenderedNum) && tenderedNum >= owed ? tenderedNum - owed : 0;

  const payCash = async () => {
    setBusy(true);
    try {
      const body = { amount: owed, client_request_id: newClientRequestId() };
      if (!Number.isNaN(tenderedNum) && tenderedNum > 0) body.tendered = tenderedNum;
      const { data } = await recordCashPayment(restaurantId, sessionId, body);
      const ch = Number(data.change || 0);
      toast.success(ch > 0
        ? t('pos.payment.cashChange', 'Cash recorded — change {{change}}', { change: formatPrice(ch, currency) })
        : t('pos.payment.cashSuccess', 'Cash payment recorded'));
      onPaid();
    } catch (err) {
      toast.error(err.response?.data?.error || t('pos.payment.cashFailed', 'Could not record cash payment.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('pos.payment.title', 'Take payment')}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 2 }}>
          <Typography sx={{ color: 'text.secondary' }}>{t('pos.owed', 'Owed')}</Typography>
          <Typography sx={{ fontSize: 24, fontWeight: 800 }}>{formatPrice(owed, currency)}</Typography>
        </Box>

        <Tabs value={method} onChange={(e, v) => setMethod(v)} sx={{ mb: 2 }}>
          <Tab value="cash" label={t('pos.payment.cash', 'Cash')} />
          {cardEnabled && <Tab value="card" label={t('pos.payment.card', 'Card')} />}
        </Tabs>

        {method === 'cash' && (
          <Box>
            <TextField
              fullWidth
              size="small"
              type="number"
              label={t('pos.payment.tendered', 'Cash tendered')}
              value={tendered}
              onChange={(e) => setTendered(e.target.value)}
              inputProps={{ min: 0, step: '0.01' }}
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography sx={{ color: 'text.secondary' }}>{t('pos.payment.change', 'Change')}</Typography>
              <Typography sx={{ fontWeight: 700 }}>{formatPrice(change, currency)}</Typography>
            </Box>
            <Button fullWidth variant="contained" onClick={payCash} disabled={busy}>
              {busy ? <CircularProgress size={22} color="inherit" /> : t('pos.payment.recordCash', 'Record cash payment')}
            </Button>
          </Box>
        )}

        {method === 'card' && cardEnabled && (
          <Elements stripe={getStripe()} options={{ locale: i18n.language }}>
            <CardForm restaurantId={restaurantId} sessionId={sessionId} owed={owed} currency={currency} onPaid={onPaid} onSubscribePayments={onSubscribePayments} />
          </Elements>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={busy}>{t('common.close', 'Close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentDialog;
